import { appOverride, appVariant } from '@util/AppUtils';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'ts-utils';
import { Color } from '../../config/Color';
import * as icons from '../assets/images/Icons';
import { BodySM, BodyXXS } from './Typography';

export enum BridgeType {
  Line,
  Plus,
}

export type StepListProps = {
  items: ItemDetails[];
  bridgeType: BridgeType;
  lastItemBridge?: boolean;
  showSmallIcons?: boolean;
};

export const StepList: FunctionComponent<StepListProps> = (props) => {
  const { items, lastItemBridge, bridgeType, showSmallIcons } = props;
  return (
    <>
      {items.map((item, index) => (
        <StepListItem
          {...item}
          key={index}
          index={index + 1}
          totalItems={items.length}
          showBridge={lastItemBridge || index !== items.length - 1}
          bridgeType={bridgeType}
          showSmallIcons={showSmallIcons ?? false}
        />
      ))}
    </>
  );
};

export const StepListTitle = styled(BodySM)`
  margin-bottom: 0.5em;
`;

type ItemDetails = {
  image?: string;
  title?: string;
  desc: string;
};

type StepListItemProps = ItemDetails & {
  index: number;
  showBridge: boolean;
  totalItems: number;
  bridgeType: BridgeType;
  showSmallIcons: boolean;
};

const StepListItem: FunctionComponent<StepListItemProps> = (props) => {
  const {
    index = 0,
    image,
    title,
    desc,
    showBridge,
    totalItems = 0,
    bridgeType,
    showSmallIcons,
  } = props;
  const imageDimensions = showSmallIcons ? '30px' : '34px';

  return (
    <StepItem>
      <>
        {!isEmpty(image) ? (
          <StepImage src={image} width={imageDimensions} height={imageDimensions} />
        ) : (
          <StepNumber intensity={0.5 + 0.5 * (index / totalItems)}>{index}</StepNumber>
        )}
        {showBridge && <Bridge bridgeType={bridgeType} />}
      </>
      <StepContainer>
        {!isEmpty(title) && <SmallText boldFontWeight>{title}:</SmallText>}
        <SmallText>{desc}</SmallText>
      </StepContainer>
    </StepItem>
  );
};

type BridgeProp = { bridgeType?: BridgeType };
const Bridge: FunctionComponent<BridgeProp> = (props) => {
  const { bridgeType = BridgeType.Line } = props;

  if (bridgeType === BridgeType.Plus) {
    const plusIcon = appVariant({
      meno: icons.plusBlue,
      nerva: icons.plus,
      quit: icons.plusGreen,
      pain: icons.plusBlue,
    });
    return <PlusImage src={plusIcon} alt="plus icon" />;
  } else {
    return <LineBridge />;
  }
};

const StepItem = styled.div`
  display: flex;
  position: relative;
  align-items: center;
`;

const StepContainer = styled.div`
  background: ${appOverride({ nerva: 'rgba(255, 255, 255, 0.08)', default: Color.surface.muted })};
  border-radius: 10px;
  padding: ${({ theme }) => `${theme.spacing._12px} ${theme.spacing._16px}`};
  margin: ${({ theme }) =>
    `${theme.spacing._6px} ${theme.spacing._2px} ${theme.spacing._6px} ${theme.spacing._16px}`};
  width: 100%;
`;

type SmallTextProp = { boldFontWeight?: boolean };
export const SmallText = styled(BodyXXS)<SmallTextProp>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin: 0;
  font-weight: ${({ boldFontWeight, theme }) => boldFontWeight && theme.fontWeight.bold};
`;

type StepNumberProp = { intensity?: number };
const StepNumber = styled.div<StepNumberProp>`
  background: ${appOverride({ nerva: Color.content.default, default: Color.decorative.onAccent })};
  opacity: ${({ intensity }) => intensity ?? 1};
  height: ${({ theme }) => theme.spacing._30px};
  width: ${({ theme }) => theme.spacing._30px};
  line-height: 32px;
  flex-shrink: 0;
  border-radius: 100%;
  text-align: center;
  color: ${appOverride({ nerva: Color.action.default, default: 'white' })};
  ${({ theme }) => appOverride({ nerva: 'box-shadow: none', default: theme.shadow.drop.gray })};
`;

const LineBridge = styled.div`
  border-radius: 5px;
  width: ${({ theme }) => theme.spacing._4px};
  height: ${({ theme }) => theme.spacing._24px};
  background: ${Color.decorative.default};
  position: absolute;
  left: ${({ theme }) => theme.spacing._12px};
  bottom: -12px;
`;

const PlusImage = styled.img`
  position: absolute;
  left: ${({ theme }) => theme.spacing._12px};
  bottom: -4px;
  width: ${({ theme }) => theme.spacing._8px};
`;

const StepImage = styled.img`
  flex-shrink: 0;
  border-radius: 100%;
  ${({ theme }) => theme.shadow.drop.lightGray}
`;
